import {
    Component,
    forwardRef,
    inject,
    Input,
    ViewChild
} from '@angular/core';
import { CommonModule } from "@angular/common";
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule
} from "@angular/forms";

import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatDatepickerInput, MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";

import { MatListModule } from "@angular/material/list";

import { TranslateModule } from "@ngx-translate/core";

import {
    DatetimePicker,
    TilbyDatePipe,
    TimePicker
} from "@tilby/tilby-ui-lib/pipes/tilby-date";
import { MatNativeDateModule } from '@angular/material/core';
import { TilbyTimeComponent } from "../tilby-time-picker";
import { CustomFormControl } from '../../utils';

@Component({
    standalone: true,
    selector: 'tilby-datetime-picker',
    templateUrl: './tilby-datetime-picker.component.html',
    imports: [
        CommonModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatCardModule,
        MatNativeDateModule,
        MatListModule,
        TranslateModule,
        TilbyDatePipe,
        MatDatepickerModule,
        ReactiveFormsModule,
        TilbyTimeComponent
    ],
    styleUrls: ['./tilby-datetime-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TilbyDatetimePickerComponent),
            multi: true,
        }
    ]
})
export class TilbyDatetimePickerComponent implements ControlValueAccessor {

    @ViewChild('timePicker') timePickerComponent!: TilbyTimeComponent;
    @ViewChild(MatDatepickerInput) dateInput!: MatDatepickerInput<any>;
    private readonly tilbyDatePipe = inject(TilbyDatePipe);
    
    protected isFocused = false;
    protected get inputType(): string {
        if (!!this.datetimePicker.date) {
            return 'datetime-local';
        }
        return 'text';
    }
    
    protected isDisabled = false;
    private _datetimePicker: DatetimePicker = { date: '', h: '00', m: '00' };

    get datetimePicker(){
        return this._datetimePicker;
    }
    set datetimePicker(dateTime:DatetimePicker){
        this._datetimePicker = dateTime;
        if(dateTime.date) this.timePickerControl.setValue(dateTime);
        else this.timePickerControl.setValue(this.setDatetimePicker(TilbyDatePipe.date()));
    }

    datetimePickerAtOpen=this.datetimePicker;
    timePickerControl = new CustomFormControl<TimePicker>(this.setDatetimePicker(TilbyDatePipe.date()));

    @Input() label: string = 'Choose date and time';
   /* @Input() set date(date: string) {
        this.datetimePicker = this.setDatetimePicker(date);
    }*/

    private setDatetimePicker(date: string): DatetimePicker {
        return {
            date: date || '',
            h: this.getShopDateTime(date, 'HH') || '00',
            m: this.getShopDateTime(date, 'mm') || '00',
        };
    }
    private getShopDateTime(date: string, format: string): string | null {
        return this.tilbyDatePipe.transform(date, format) || '';
    }

    setDatePicker({value: newDatePicked}: {value:string}) {
        const timeValues = this.timePickerControl.value;
        
        this._datetimePicker.h = timeValues.h;
        this._datetimePicker.m = timeValues.m;
        
        const stringDate = TilbyDatePipe.setDatepickerTime({...timeValues, date: newDatePicked});
        this.setShopTimezone(stringDate);
        
        this.datetimePickerAtOpen = { ...this.datetimePicker };
        this._onChange(this.datetimePicker.date);
    }

    setShopTimezone(stringDate: string) {
        this.datetimePicker.date = TilbyDatePipe.shopDate({date: stringDate, directViewInput: true}) || '';
    }

//START - ControlValueAccessor
    public writeValue(date: string): void {
        this.datetimePicker = this.setDatetimePicker(date || '');
        this.forceRefresh();
    }

    private _onChange = (_value: string | null): void => undefined;
    public registerOnChange(fn: (value: string | null) => void): void {
        this._onChange = (value: string | null) => {
            fn(value);
        };
    }

    public onTouched = (): void => undefined;
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }
// END - ControlValueAccessor

    updatePicker($event: any) {
        const value = $event.target?.value;
        if (value) {
            this.datetimePicker = this.setDatetimePicker(value);
            this._onChange(this.datetimePicker.date);
        } else {
            this.clear();
        }
    }

    clear(event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        this.datetimePicker = { date: '', h: '00', m: '00' };
        this.datetimePickerAtOpen = { ...this.datetimePicker };
        this.forceRefresh();

        this._onChange(null);
        this.onTouched();
    }

    // Re-sync the (persistent) hidden datepicker input with the current value.
    // The input must not be recreated: a MatDatepicker accepts a single input only.
    forceRefresh() {
        setTimeout(() => {
            this.dateInput.value = this.tilbyDatePipe.transform(this.timePickerControl.value?.date, 'yyyy-MM-dd') || null;
        }, 0);
    }

    onDatePickerCancel() {
        this.datetimePicker = { ...this.datetimePickerAtOpen };
    }

    onClosed() {
        this.datetimePickerAtOpen = { ...this.datetimePicker }; 
    }

    onFocus() {
        this.isFocused = true;
        this.datetimePickerAtOpen = { ...this.datetimePicker };
        if (this.timePickerComponent) {
            this.timePickerComponent.refreshNow();
        }
    }

    onBlur() {
        this.isFocused = false;
        this.onTouched();
        this.onClosed(); 
    }
}

